import Phaser from "phaser";

export type BoarEventType = "hit" | "golden" | "wrong" | "escape";
type EntityKind = "boar" | "golden" | "rabbit";

const SPAWN_START_MS = 700;
const SPAWN_MIN_MS = 220;
const SPAWN_STEP_MS = 30;
const SPEED_DOWN_INTERVAL_MS = 2000;
const BOAR_MARGIN_PX = 44;

const BASE_SIZE_PX = 44;
const MIN_SIZE_PX = 26;
const GOLDEN_VISIBLE_FRACTION = 0.55; // il dorato sparisce prima del normale intervallo di spawn

// Soglie di punteggio per salire di livello. Indice nell'array = livello - 1.
export const LEVEL_THRESHOLDS = [0, 5, 10, 15, 20];

export function getLevelForScore(score: number): number {
  let lvl = 1;
  for (let i = 1; i < LEVEL_THRESHOLDS.length; i++) {
    if (score >= LEVEL_THRESHOLDS[i]) lvl = i + 1;
  }
  return lvl;
}

/**
 * Scena Phaser che gestisce la comparsa/scomparsa di cinghiali, conigli e
 * cinghiali dorati. Non gestisce punteggio "ufficiale", timer o classifica:
 * notifica ogni evento al componente React tramite onEvent, che decide come
 * tradurlo in punti (bonus, penalità, moltiplicatore combo, ecc).
 * Tiene comunque un punteggio interno "grezzo" (senza moltiplicatori) solo
 * per calcolare il proprio livello di difficoltà.
 */
export default class BoarHuntScene extends Phaser.Scene {
  private onEvent: (type: BoarEventType) => void;
  private current?: { obj: Phaser.GameObjects.Text; kind: EntityKind };
  private spawnEvent?: Phaser.Time.TimerEvent;
  private speedDownEvent?: Phaser.Time.TimerEvent;
  private goldenTimer?: Phaser.Time.TimerEvent;
  private speedMs = SPAWN_START_MS;
  private internalScore = 0;

  constructor(onEvent: (type: BoarEventType) => void) {
    super("boar-hunt");
    this.onEvent = onEvent;
  }

  create() {
    this.cameras.main.setBackgroundColor(0x14532d);

    for (let i = 0; i < 18; i++) {
      const x = Phaser.Math.Between(0, this.scale.width);
      const y = Phaser.Math.Between(0, this.scale.height);
      this.add.text(x, y, "🌿", { fontSize: "16px" }).setAlpha(0.35);
    }

    this.scheduleSpawn();
    this.speedDownEvent = this.time.addEvent({
      delay: SPEED_DOWN_INTERVAL_MS,
      loop: true,
      callback: () => {
        this.speedMs = Math.max(SPAWN_MIN_MS, this.speedMs - SPAWN_STEP_MS);
        this.scheduleSpawn();
      },
    });
  }

  private get level(): number {
    return getLevelForScore(this.internalScore);
  }

  private scheduleSpawn() {
    this.spawnEvent?.remove(false);
    this.spawnEvent = this.time.addEvent({
      delay: this.speedMs,
      loop: true,
      callback: () => this.spawnEntity(),
    });
  }

  private pickKind(): EntityKind {
    const lvl = this.level;
    const rabbitChance = Math.min(0.32, 0.12 + lvl * 0.04);
    const goldenChance = Math.min(0.18, 0.06 + lvl * 0.02);
    const r = Math.random();
    if (r < rabbitChance) return "rabbit";
    if (r < rabbitChance + goldenChance) return "golden";
    return "boar";
  }

  private spawnEntity() {
    this.goldenTimer?.remove(false);

    // L'entità precedente, se non colpita, "scappa".
    // Un coniglio scappato è corretto (va ignorato): nessun evento, nessuna rottura di serie.
    if (this.current) {
      const { obj, kind } = this.current;
      obj.disableInteractive();
      this.tweens.add({
        targets: obj,
        scale: 0,
        alpha: 0,
        duration: 150,
        onComplete: () => obj.destroy(),
      });
      if (kind !== "rabbit") this.onEvent("escape");
      this.current = undefined;
    }

    const kind = this.pickKind();
    const emoji = kind === "rabbit" ? "🐰" : kind === "golden" ? "🐗✨" : "🐗";
    const size = Math.max(MIN_SIZE_PX, BASE_SIZE_PX - (this.level - 1) * 4);

    const width = this.scale.width;
    const height = this.scale.height;
    const x = Phaser.Math.Between(BOAR_MARGIN_PX, Math.max(BOAR_MARGIN_PX, width - BOAR_MARGIN_PX));
    const y = Phaser.Math.Between(BOAR_MARGIN_PX, Math.max(BOAR_MARGIN_PX, height - BOAR_MARGIN_PX));

    const obj = this.add
      .text(x, y, emoji, { fontSize: `${size}px` })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setScale(0);

    this.tweens.add({ targets: obj, scale: 1, duration: 150, ease: "Back.Out" });

    obj.on("pointerdown", () => {
      if (!obj.active) return;
      this.goldenTimer?.remove(false);

      if (kind === "rabbit") {
        this.internalScore = Math.max(0, this.internalScore - 1);
        this.onEvent("wrong");
      } else if (kind === "golden") {
        this.internalScore += 3;
        this.onEvent("golden");
      } else {
        this.internalScore += 1;
        this.onEvent("hit");
      }

      obj.disableInteractive();
      this.tweens.add({
        targets: obj,
        scale: 1.5,
        alpha: 0,
        duration: 180,
        onComplete: () => obj.destroy(),
      });
      if (this.current?.obj === obj) this.current = undefined;
    });

    this.current = { obj, kind };

    // Il cinghiale dorato sparisce prima del normale intervallo di spawn,
    // per premiare solo chi reagisce rapidamente.
    if (kind === "golden") {
      this.goldenTimer = this.time.delayedCall(this.speedMs * GOLDEN_VISIBLE_FRACTION, () => {
        if (this.current?.obj === obj && obj.active) {
          obj.disableInteractive();
          this.tweens.add({
            targets: obj,
            scale: 0,
            alpha: 0,
            duration: 150,
            onComplete: () => obj.destroy(),
          });
          this.onEvent("escape");
          this.current = undefined;
        }
      });
    }
  }

  stopAll() {
    this.spawnEvent?.remove(false);
    this.speedDownEvent?.remove(false);
    this.goldenTimer?.remove(false);
    if (this.current) {
      this.current.obj.destroy();
      this.current = undefined;
    }
  }
}